import { useLatest } from '@qfa/react-components';
import { ColumnConfig, UpdateConfig } from 'components/Table/contexts';
import ColumnControlsContext, {
  ColumnsContextState,
} from 'components/Table/contexts/ColumnControlsContext/ColumnControlsContext';
import ColumnsContext from 'components/Table/contexts/ColumnsContext';
import { BaseRow } from 'components/Table/types';
import { columnToJson, orderColumns } from 'components/Table/utils';
import { isEqual } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Controller } from '../types';
import { ColumnControlsProps, ControlComponentProps } from './types';
import { useColumnsRef } from './useColumnRef';

const ColumnControls: React.FunctionComponent<
  ColumnControlsProps & Pick<ColumnsContextState, 'columns' | 'setColumns'>
> = ({
  children,
  onChange,
  columnPreferenceKey,
  component: Component,
  open = false,
  setOpen,
  setColumns,
  columns,
  reference,
  showSearch,
  drawerClass = '',
  columnsRef,
}) => {
  const { Provider } = ColumnControlsContext;

  const [controls, setControls] = useState<Controller[]>([]);

  const registerControl = useCallback((payload: Controller) => {
    const { id } = payload;
    setControls((prev) => {
      const foundIndex = prev.findIndex((control) => control.id === id);
      // add to controls if not present
      if (foundIndex < 0) {
        return [...prev, payload];
      }
      // update if present
      const newControls = [...prev];
      const newControl = {
        ...prev[foundIndex],
        ...payload,
      };
      newControls.splice(foundIndex, 1, newControl);
      return newControls;
    });
  }, []);

  const unregisterControl = useCallback((payload: Controller) => {
    const { id } = payload;
    setControls((prev) => {
      return prev.filter((control) => control.id !== id);
    });
  }, []);

  const updateColumn = useCallback(
    (...updates: UpdateConfig<BaseRow>[]) => {
      setColumns((prev) => {
        const newColumns = [...prev];
        updates.forEach((columnChanges) => {
          const foundIndex = prev.findIndex((col) => col.name === columnChanges.name);
          const newColumn = {
            ...prev[foundIndex],
            ...columnChanges,
          } as ColumnConfig;
          if (foundIndex > -1) {
            newColumns.splice(foundIndex, 1, newColumn);
          } else {
            newColumns.push(newColumn);
          }
        });
        return newColumns;
      });
    },
    [setColumns]
  );

  const getControl = useCallback(
    (controlId: string) => {
      return controls.find((control) => control.id === controlId);
    },
    [controls]
  );

  const controlledColumns = useMemo(() => {
    const noControls = !controls.length;
    if (noControls) {
      return columns;
    }
    const controlledNames = columns.reduce((acc, column) => {
      const isControlledColumn = controls.some((control) => control.hasControl(column.name));
      // ensure the column isn't configured to disallow control
      if (isControlledColumn && !column.noControl) {
        acc.push(column.name);
      }
      return acc;
    }, [] as string[]);

    return columns.filter((column) => controlledNames.includes(column.name));
  }, [controls, columns]);

  const latestOnChange = useLatest(onChange);
  const prevColumnsJson = useRef(columns.map(columnToJson));

  useColumnsRef({
    columnsRef,
  });

  useEffect(() => {
    const nextColumnsJson = columns.map(columnToJson);

    if (!isEqual(nextColumnsJson, prevColumnsJson.current)) {
      if (
        // dont expose our first state
        prevColumnsJson.current.length
      ) {
        latestOnChange.current?.(nextColumnsJson);
      }
      prevColumnsJson.current = nextColumnsJson;
    }
  }, [columns, columnsRef, latestOnChange]);

  const ComponentProps: ControlComponentProps = {
    getControl,
    controlledColumns,
    open,
    setOpen,
    columnPreferenceKey,
    reference,
    showSearch,
    drawerClass,
  };

  const orderedColumns = useMemo(() => {
    return orderColumns(controlledColumns);
  }, [controlledColumns]);

  return (
    <Provider
      value={{ controlledColumns, controls, updateColumn, registerControl, unregisterControl }}
    >
      <Component key='controlsComponent' {...ComponentProps} controlledColumns={orderedColumns} />
      {children}
    </Provider>
  );
};

const Memo = React.memo(ColumnControls, isEqual);

const ContextMemo = (props: ColumnControlsProps) => {
  const { setColumns, columns } = useContext(ColumnsContext);
  return <Memo {...props} setColumns={setColumns} columns={columns} />;
};

export default ContextMemo;
